import { Button, Checkbox, Label, Paragraph, Text, XStack, YStack, type XStackProps } from 'tamagui';

export interface ListItemProps extends Omit<XStackProps, 'children' | 'onPress'> {
  id: string;
  title: string;
  checked: boolean;
  onToggle: (next: boolean) => void;
  onTitlePress?: () => void;
  quantity?: number;
  description?: string;
  /** When true, the row is visually highlighted (orange tint). */
  missing?: boolean;
  /** If provided, shows a ⚠ toggle to mark/unmark the item as missing. */
  onMissingToggle?: (next: boolean) => void;
}

/**
 * Single shopping-list item row: a checkbox + title. Optionally shows a
 * quantity prefix ("×3") and a description line below. Keeps presentation in
 * the ui package so web and mobile render identically and product code only
 * has to deal with state.
 */
export function ListItem({
  id,
  title,
  checked,
  onToggle,
  onTitlePress,
  quantity,
  description,
  missing,
  onMissingToggle,
  ...rest
}: ListItemProps) {
  const showQuantity = quantity !== undefined && quantity > 1;
  return (
    <XStack
      alignItems="flex-start"
      gap="$3"
      paddingVertical="$2"
      paddingHorizontal={missing ? '$2' : undefined}
      borderRadius={missing ? '$2' : undefined}
      backgroundColor={missing ? '$orange3' : undefined}
      borderLeftWidth={missing ? 3 : 0}
      borderLeftColor={missing ? '$orange9' : undefined}
      {...rest}
    >
      <Checkbox
        id={id}
        size="$4"
        checked={checked}
        onCheckedChange={(value) => onToggle(value === true)}
        marginTop="$1"
      >
        <Checkbox.Indicator>
          <Text fontSize="$3" lineHeight="$1" fontWeight="700">
            ✓
          </Text>
        </Checkbox.Indicator>
      </Checkbox>
      <YStack flex={1} gap="$1" onPress={onTitlePress} cursor={onTitlePress ? 'pointer' : undefined}>
        <Label
          htmlFor={id}
          fontSize="$5"
          textDecorationLine={checked ? 'line-through' : 'none'}
          opacity={checked ? 0.6 : 1}
        >
          {showQuantity ? `${quantity}× ${title}` : title}
        </Label>
        {description ? (
          <Paragraph size="$2" opacity={checked ? 0.5 : 0.7}>
            {description}
          </Paragraph>
        ) : null}
      </YStack>
      {onMissingToggle ? (
        <Button
          size="$2"
          chromeless
          aria-label={missing ? 'Markierung als fehlend aufheben' : 'Als fehlend markieren'}
          onPress={() => onMissingToggle(!missing)}
          opacity={missing ? 1 : 0.4}
        >
          ⚠
        </Button>
      ) : null}
    </XStack>
  );
}
